import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Sparkles, BookOpen, CheckCircle2, XCircle, ChevronDown, ChevronRight,
  Loader2, RefreshCw, Building2, User as UserIcon, Home as HomeIcon, FileText,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';

/**
 * IntakeFactsV2Section — fila de revisão dos `intake_facts` (Knowledge-First V2).
 *
 * Por trás:
 *  - Lê fatos com `policy_decision IN ('auto_propose','auto_commit','human_review','block')`
 *    de runs com pelo menos 1 fato pendente. Agrupa por entidade primária resolvida
 *    (priority property → company → person, mesma regra de fn_intake_v2_apply_phase_3).
 *  - Aprovar (auto_propose → auto_commit): INSERT em `knowledge_atoms`,
 *    UPDATE `intake_facts.policy_decision='auto_commit'`, INSERT `policy_observations`
 *    (delta_action='confirmed'), recalcula counters em `intake_runs`.
 *  - Rejeitar (auto_propose → block): UPDATE `intake_facts.policy_decision='block'`,
 *    INSERT `policy_observations` (delta_action='reverted'), recalcula counters.
 *  - auto_commit (já aplicados): exibidos read-only com badge verde.
 *
 * Esta seção só monta se `FEATURE_FLAGS.INTAKE_V2_LAND_TITLE` estiver ativo (decisão
 * fica no chamador — `IntakeReviewQueue`).
 */

const ENTITY_ICON = { property: HomeIcon, company: Building2, person: UserIcon };

const fmtPct = (v) => (v == null ? '—' : `${Math.round(Number(v) * 100)}%`);

const ConfidenceBar = ({ value }) => {
  const pct = Math.max(0, Math.min(1, Number(value) || 0));
  const tone = pct >= 0.9 ? 'bg-emerald-500' : pct >= 0.75 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-14 h-1.5 bg-slate-200 rounded overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct * 100}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-slate-600">{fmtPct(pct)}</span>
    </div>
  );
};

const FactTypeBadge = ({ type }) => {
  if (!type) return null;
  const tone = {
    identification: 'bg-indigo-100 text-indigo-800',
    dimensional:    'bg-sky-100 text-sky-800',
    ownership_event:'bg-emerald-100 text-emerald-800',
    contract_event: 'bg-violet-100 text-violet-800',
    encumbrance:    'bg-amber-100 text-amber-800',
    financial:      'bg-rose-100 text-rose-800',
  }[type] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${tone}`}>
      {type.replace('_', ' ')}
    </span>
  );
};

const SourceExcerpt = ({ excerpt, page }) => {
  const [expanded, setExpanded] = useState(false);
  if (!excerpt) return null;
  const trunc = excerpt.length > 160 ? `${excerpt.slice(0, 160)}…` : excerpt;
  return (
    <div className="text-[11px] text-slate-600 italic flex items-start gap-1 mt-1">
      <BookOpen size={11} className="text-slate-400 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <span>"{expanded || excerpt.length <= 160 ? excerpt : trunc}"</span>
        {page != null && <span className="text-[10px] text-slate-400 ml-1">(p. {page})</span>}
        {excerpt.length > 160 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="ml-1 text-indigo-600 hover:text-indigo-800 not-italic"
          >
            {expanded ? 'menos' : 'mais'}
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Mapeamento entity_type da intake_entities → singular usado em knowledge_atoms ── */
const ENTITY_TABLE_SINGULAR = {
  property: 'imovel',
  company:  'empresa',
  person:   'contato',
};
const ENTITY_TYPE_PRIORITY = { property: 1, company: 2, person: 3 };

/**
 * Replica a lógica do CTE `primary_entity` em fn_intake_v2_apply_phase_3:
 * para um run, escolhe a entidade resolvida de maior prioridade
 * (property > company > person).
 */
const pickPrimaryEntity = (entitiesOfRun) => {
  const matched = entitiesOfRun.filter(
    (e) => e.matched_entity_id && ENTITY_TYPE_PRIORITY[e.entity_type] != null,
  );
  if (matched.length === 0) return null;
  matched.sort((a, b) => ENTITY_TYPE_PRIORITY[a.entity_type] - ENTITY_TYPE_PRIORITY[b.entity_type]);
  return matched[0];
};

/**
 * Chave de agrupamento para a UI: prefere matched_entity_id, cai para o nome
 * normalizado da primeira entidade do array `fact.entities`.
 */
const factGroupKey = (fact, primaryByRun) => {
  const primary = primaryByRun.get(fact.intake_run_id);
  if (primary?.matched_entity_id) {
    return {
      key: `${primary.entity_type}:${primary.matched_entity_id}`,
      label: primary.extracted_data?.nome
        || primary.extracted_data?.matricula
        || primary.extracted_data?.company_name
        || primary.extracted_data?.first_name
        || `${primary.entity_type} (matched)`,
      type: primary.entity_type,
    };
  }
  // Fallback: usa primeira entidade do JSONB do fato.
  const first = Array.isArray(fact.entities) && fact.entities[0];
  if (first?.name) {
    return {
      key: `unmatched:${first.type || 'misc'}:${first.name}`,
      label: first.name,
      type: first.type || 'misc',
    };
  }
  return { key: `run:${fact.intake_run_id}`, label: '(sem entidade resolvida)', type: 'misc' };
};

const FactRow = ({ fact, busy, readonly, onApprove, onReject }) => (
  <div className="px-3 py-2 border-b border-slate-100 last:border-b-0">
    <div className="flex items-start gap-2">
      <Sparkles size={12} className="text-indigo-500 flex-shrink-0 mt-0.5" />
      <div className="flex-grow min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <FactTypeBadge type={fact.fact_type} />
          {readonly && (
            <span className="text-[10px] bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5 font-semibold">
              ✅ Aplicado
            </span>
          )}
          <span className="ml-auto"><ConfidenceBar value={fact.extraction_confidence} /></span>
        </div>
        <div className="text-xs text-slate-800 mt-0.5 line-clamp-3" title={fact.content || ''}>
          {fact.content}
        </div>
        {fact.final_confidence != null && fact.final_confidence !== fact.extraction_confidence && (
          <div className="text-[10px] text-slate-500 mt-0.5">
            Final: {fmtPct(fact.final_confidence)}
            {fact.identifier_strength != null && ` · id_strength ${fmtPct(fact.identifier_strength)}`}
          </div>
        )}
        <SourceExcerpt excerpt={fact.source_excerpt} page={fact.source_page} />
      </div>
      {!readonly && (
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            type="button"
            disabled={busy}
            onClick={() => onApprove(fact)}
            className="px-2 py-0.5 text-[10px] bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
          >
            <CheckCircle2 size={11} /> Aprovar
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onReject(fact)}
            className="px-2 py-0.5 text-[10px] bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50 flex items-center gap-1"
          >
            <XCircle size={11} /> Rejeitar
          </button>
        </div>
      )}
    </div>
  </div>
);

const EntityGroupCard = ({ group, onApproveFact, onRejectFact, busy }) => {
  const [expanded, setExpanded] = useState(true);
  const Icon = ENTITY_ICON[group.type] || FileText;
  const proposed = group.facts.filter((f) => f.policy_decision === 'auto_propose');
  const committed = group.facts.filter((f) => f.policy_decision === 'auto_commit');

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-50"
      >
        {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        <Icon size={14} className="text-indigo-600 flex-shrink-0" />
        <span className="text-sm font-semibold text-slate-800 truncate" title={group.label}>
          {group.label}
        </span>
        <div className="ml-auto flex items-center gap-2 text-[11px] text-slate-500">
          {proposed.length > 0 && (
            <span className="bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 font-medium">
              {proposed.length} pend.
            </span>
          )}
          {committed.length > 0 && (
            <span className="bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5 font-medium">
              {committed.length} aplic.
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-200">
          {proposed.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Pendentes (auto_propose) — {proposed.length}
              </div>
              {proposed.map((f) => (
                <FactRow
                  key={f.id}
                  fact={f}
                  busy={busy}
                  readonly={false}
                  onApprove={onApproveFact}
                  onReject={onRejectFact}
                />
              ))}
            </div>
          )}
          {committed.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                Aplicados automaticamente (auto_commit) — {committed.length}
              </div>
              {committed.map((f) => (
                <FactRow key={f.id} fact={f} busy={busy} readonly />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const IntakeFactsV2Section = () => {
  const { user } = useAuth();
  const [facts, setFacts] = useState([]);
  const [entitiesByRun, setEntitiesByRun] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Pega fatos (auto_propose pendentes + auto_commit recentes para contexto).
      const { data: factsData, error: factsErr } = await supabase
        .from('intake_facts')
        .select('*')
        .in('policy_decision', ['auto_propose', 'auto_commit'])
        .order('intake_run_id', { ascending: false })
        .order('source_page', { ascending: true });
      if (factsErr) throw factsErr;

      const runIds = Array.from(new Set((factsData || []).map((f) => f.intake_run_id)));
      let entsByRun = new Map();
      if (runIds.length > 0) {
        const { data: entsData, error: entsErr } = await supabase
          .from('intake_entities')
          .select('id, run_id, entity_type, matched_entity_id, matched_entity_table, extracted_data, resolution_status')
          .in('run_id', runIds);
        if (entsErr) throw entsErr;
        for (const e of entsData || []) {
          if (!entsByRun.has(e.run_id)) entsByRun.set(e.run_id, []);
          entsByRun.get(e.run_id).push(e);
        }
      }

      setFacts(factsData || []);
      setEntitiesByRun(entsByRun);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const primaryByRun = useMemo(() => {
    const map = new Map();
    for (const [runId, ents] of entitiesByRun) {
      const primary = pickPrimaryEntity(ents);
      if (primary) map.set(runId, primary);
    }
    return map;
  }, [entitiesByRun]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const fact of facts) {
      const meta = factGroupKey(fact, primaryByRun);
      if (!map.has(meta.key)) {
        map.set(meta.key, { key: meta.key, label: meta.label, type: meta.type, facts: [] });
      }
      map.get(meta.key).facts.push(fact);
    }
    // Ordena: maior nº de pendentes primeiro
    return Array.from(map.values()).sort((a, b) => {
      const ap = a.facts.filter((f) => f.policy_decision === 'auto_propose').length;
      const bp = b.facts.filter((f) => f.policy_decision === 'auto_propose').length;
      if (bp !== ap) return bp - ap;
      return a.label.localeCompare(b.label);
    });
  }, [facts, primaryByRun]);

  const recalcRunCounters = useCallback(async (runId) => {
    const { data, error: countErr } = await supabase
      .from('intake_facts')
      .select('policy_decision', { head: false })
      .eq('intake_run_id', runId);
    if (countErr) throw countErr;
    const counts = { auto_commit: 0, auto_propose: 0, block: 0 };
    for (const r of data || []) {
      if (counts[r.policy_decision] != null) counts[r.policy_decision] += 1;
    }
    const { error: upErr } = await supabase
      .from('intake_runs')
      .update({
        facts_auto_committed: counts.auto_commit,
        facts_staged: counts.auto_propose,
        facts_rejected: counts.block,
        updated_at: new Date().toISOString(),
      })
      .eq('id', runId);
    if (upErr) throw upErr;
  }, []);

  const handleApprove = useCallback(async (fact) => {
    setBusy(true); setError(null);
    try {
      const primary = primaryByRun.get(fact.intake_run_id);
      if (!primary || !primary.matched_entity_id) {
        throw new Error('Não há entidade primária resolvida neste run; não é possível anexar o fato a knowledge_atoms.');
      }
      const entityTypeSingular = ENTITY_TABLE_SINGULAR[primary.entity_type];
      // 1) INSERT knowledge_atoms — replica fn_intake_v2_apply_phase_3, mas atribui ao humano.
      const insertPayload = {
        atom_type: 'fact',
        category: fact.fact_type,
        knowledge_class: 'fact',
        content: fact.content,
        source_type: 'document',
        source_intake_run_id: fact.intake_run_id,
        source_page: fact.source_page != null ? String(fact.source_page) : null,
        source_excerpt: fact.source_excerpt,
        entity_type: entityTypeSingular,
        entity_id: primary.matched_entity_id,
        confidence: fact.final_confidence != null ? fact.final_confidence : fact.extraction_confidence,
        extraction_method: 'knowledge_first_v2',
        status: 'active',
        extraction_model: fact.extraction_metadata?.model || null,
        prompt_version: fact.extraction_metadata?.prompt_version || null,
        created_by: 'intake_v2_human_approve',
        created_by_agent: 'agent_extract_facts_v2',
        verified_by: user?.email || 'unknown',
        verified_at: new Date().toISOString(),
      };
      const { error: insErr } = await supabase
        .from('knowledge_atoms')
        .insert(insertPayload);
      if (insErr) throw insErr;

      // 2) UPDATE intake_facts policy_decision → auto_commit (idempotência)
      const { error: factErr } = await supabase
        .from('intake_facts')
        .update({
          policy_decision: 'auto_commit',
          updated_at: new Date().toISOString(),
          updated_by: user?.email || 'unknown',
        })
        .eq('id', fact.id);
      if (factErr) throw factErr;

      // 3) INSERT policy_observations — humano confirmou a proposta da policy
      const { error: obsErr } = await supabase
        .from('policy_observations')
        .insert({
          intake_fact_id: fact.id,
          fact_type: fact.fact_type,
          policy_decision: 'auto_propose',
          human_decision: 'approve',
          delta_action: 'confirmed',
          final_confidence: fact.final_confidence,
          observer: user?.email || 'unknown',
        });
      if (obsErr) throw obsErr;

      // 4) Recalcula contadores no run
      await recalcRunCounters(fact.intake_run_id);

      await fetchAll();
    } catch (err) {
      setError(`Aprovar fato falhou: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  }, [primaryByRun, user, recalcRunCounters, fetchAll]);

  const handleReject = useCallback(async (fact) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Motivo da rejeição (opcional):');
    if (reason === null) return;
    setBusy(true); setError(null);
    try {
      // 1) UPDATE intake_facts → block
      const { error: factErr } = await supabase
        .from('intake_facts')
        .update({
          policy_decision: 'block',
          updated_at: new Date().toISOString(),
          updated_by: user?.email || 'unknown',
        })
        .eq('id', fact.id);
      if (factErr) throw factErr;

      // 2) INSERT policy_observations — humano discordou da policy
      const { error: obsErr } = await supabase
        .from('policy_observations')
        .insert({
          intake_fact_id: fact.id,
          fact_type: fact.fact_type,
          policy_decision: 'auto_propose',
          human_decision: 'reject',
          delta_action: 'reverted',
          final_confidence: fact.final_confidence,
          delta_value_before: { reason: reason || null },
          observer: user?.email || 'unknown',
        });
      if (obsErr) throw obsErr;

      // 3) Recalcula contadores
      await recalcRunCounters(fact.intake_run_id);

      await fetchAll();
    } catch (err) {
      setError(`Rejeitar fato falhou: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  }, [user, recalcRunCounters, fetchAll]);

  const totalPending = facts.filter((f) => f.policy_decision === 'auto_propose').length;
  const totalCommitted = facts.filter((f) => f.policy_decision === 'auto_commit').length;

  return (
    <section className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-indigo-700" />
          <h2 className="text-sm font-semibold text-indigo-900">Knowledge-First V2 — fatos</h2>
          <span className="text-[10px] font-medium text-indigo-800 bg-white border border-indigo-200 px-1.5 py-0.5 rounded">
            {loading ? '…' : `${totalPending} pend · ${totalCommitted} aplic`}
          </span>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="text-[11px] px-2 py-1 bg-white border border-indigo-200 rounded text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 flex items-center gap-1"
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Atualizar
        </button>
      </div>

      {error && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-[11px] text-red-800">
          {error}
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div className="bg-white border border-indigo-100 rounded p-6 text-center text-xs text-slate-500">
          Nenhum fato V2 para revisar. 🎉
        </div>
      )}

      {!loading && groups.length > 0 && (
        <div className="space-y-2">
          {groups.map((g) => (
            <EntityGroupCard
              key={g.key}
              group={g}
              busy={busy}
              onApproveFact={handleApprove}
              onRejectFact={handleReject}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default IntakeFactsV2Section;
